// This component should support multiple image size style so possibly some of the styles will not be used.
/* eslint-disable relay/unused-fields */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as ArticleImageProps } from "~/components/article/article-image";
import { ArticleImage } from "~/components/article/article-image";
import type { Props as BaseImageProps } from "~/components/common/base-image";
import { useResponsiveImage } from "~/components/common/base-image/hooks";
import { useImageUtils } from "~/lib/hooks";
import type { ResponsiveVariants } from "~/lib/styles/responsive";
import type { articleLeadingImagesContent$key } from "~/queries/__generated__/articleLeadingImagesContent.graphql";

import { ArticleLeadingImageContainer } from "./styles";

export type Props = {
  imageProps: Omit<BaseImageProps, "$aspectRatio">;
  reference: articleLeadingImagesContent$key;
  resizeImageWidth?: number;
  responsiveVariants: ResponsiveVariants<"size1200x800" | "size768x768">;
} & Pick<ArticleImageProps, "responsiveDescriptionVariants" | "responsiveImageVariants">;

export const ArticleLeadingImages: FunctionComponent<Props> = ({
  imageProps,
  reference: reference_,
  resizeImageWidth,
  responsiveDescriptionVariants,
  responsiveImageVariants,
  responsiveVariants,
}) => {
  const content = useFragment(
    graphql`
      fragment articleLeadingImagesContent on Content {
        images {
          title
          url
          isSlideshow
          size1200x800: style(filter: { style: "1200x800" }) {
            url
          }
          size768x768: style(filter: { style: "768x768" }) {
            url
          }
        }
      }
    `,
    reference_,
  );
  const { getResizedImageUrl } = useImageUtils();

  // TODO: Support multiple images (Carousel)
  const mainImage = content.images?.find(image => image?.isSlideshow) ?? first(content.images);

  const variantSpecs = {
    size1200x800: { aspectRatio: "3/2", url: mainImage?.size1200x800?.url, width: 1200 },
    size768x768: { aspectRatio: "1/1", url: mainImage?.size768x768?.url, width: 768 },
  };

  const sources: { url?: string; width: number }[] = Object.values(variantSpecs).map(variant => ({
    url: variant.url ?? undefined,
    width: variant.width,
  }));

  const mapVariant = (viewportSize: "desktopUp" | "tabletUp" | "mobileUp") => ({
    aspectRatio: variantSpecs[responsiveVariants[viewportSize]].aspectRatio,
    width: variantSpecs[responsiveVariants[viewportSize]].width,
  });

  const responsiveVariant = {
    desktopUp: mapVariant("desktopUp"),
    mobileUp: mapVariant("mobileUp"),
    tabletUp: mapVariant("tabletUp"),
  };

  const { bindResponsiveAspectRatio, bindResponsiveImage } = useResponsiveImage(
    sources,
    responsiveVariant,
  );
  if (!mainImage) return null;

  const renderImage = () => {
    let imageUrl = mainImage[responsiveVariants.mobileUp]?.url ?? mainImage.url;

    imageUrl = resizeImageWidth ? getResizedImageUrl(imageUrl, resizeImageWidth) : imageUrl;
    return (
      <ArticleImage
        imageProps={{
          ...imageProps,
          $objectFit: "cover",
          src: imageUrl,
          ...bindResponsiveImage,
          ...bindResponsiveAspectRatio,
        }}
        responsiveDescriptionVariants={responsiveDescriptionVariants}
        responsiveImageVariants={responsiveImageVariants}
        title={mainImage.title ?? ""}
      />
    );
  };
  return <ArticleLeadingImageContainer>{renderImage()}</ArticleLeadingImageContainer>;
};

ArticleLeadingImages.displayName = "ArticleLeadingImages";
